import React, { useState, useCallback, useMemo, memo } from "react"
import activityTypes from "fixtures/activityTypes"
import moment from "moment"
import {
  DirectionsRun as ActivityIcon,
  DoubleArrow as PriorityIcon,
  ScheduleSharp as ScheduledIcon,
} from '@material-ui/icons'
import {
  AvatarInitial,
  Text,
  FileListView,
  List,
  BoardCardItem
} from "."
import Skeleton from '@material-ui/lab/Skeleton'
import { Chip, Checkbox, Box, Box as MuiBox, CircularProgress, useTheme } from "@material-ui/core"
import { getStyleProgressStatus, getCustomTextStyle, resolveColor } from "shared/utils"
import { useAppContext } from "App"
import formatValue from "../utils/formatValue"
import useStyles from "./styles"

const ACTIVITY_INFO_LIST = [
  {
    key: 'scheduled_activities_count',
    icon: ScheduledIcon,
    color: '#ff6f00'
  },
  {
    key: 'activities_count',
    icon: ActivityIcon,
    color: '#aaa'
  },
  {
    key: 'comments_count',
    icon: (activityTypes['chat'] || {}).icon,
    color: '#aaa'
  }
]

const getUserName = (user) => {
  if (!user) return ''

  let name = ''
  if (user?.first_name || user?.last_name) {
    name = `${user.first_name || ""} ${user.last_name || ""}`.trim()
  }

  if (!name) {
    name = `Contact ${user?.uid % 9999}`
  }

  return name
}

const getFieldType = (type) => {
  const types = ['resolution', 'issue_type']
  if (types.some(t => t === type))
    return 'option'

  return type
}

const getPriorityStyle = (key) => {
  let style = {}
  const base = { display: 'inline-block', position: 'relative' }

  if (key === 'high') {
    style = { ...base, color: '#E57373', transform: 'rotate(270deg)' }
  } else if (key === 'low') {
    style = { ...base, color: 'green', transform: 'rotate(90deg)', marginLeft: -2, marginRight: 4 }
  } else if (key === 'trivial') {
    style = { ...base, color: 'grey', transform: 'rotate(90deg)', marginLeft: -2, marginRight: 4 }
  } else if (['blocker', 'critical'].includes(key)) {
    style = { ...base, color: '#B71C1C', marginRight: 4 }
  } else {
    style = { ...base, top: 4, color: 'rgb(255, 179, 0)', marginRight: 2 }
  }

  return style
}

const getFieldPadding = (type, key) => {
  if (getFieldType(type) === 'user' && key === 'assigned_to') {
    return '3px'
  } else if (getFieldType(type) === 'richtext') {
    return '3px'
  } else return 0
}

const renderFieldComponent = (item, field, classes) => {
  if (field.component === 'ActivityInfo') {
    return <ActivityInfo item={item} classes={classes} />
  }
  return ''
}

// Memoized individual field renderer components
const ActivityInfo = memo(({ item, classes }) => {
  const visibleActivities = useMemo(() =>
    ACTIVITY_INFO_LIST.filter(f => item[f.key]),
    [item]
  )

  if (visibleActivities.length === 0) return null

  return (
    <div style={{ display: 'flex', flexDirection: 'row' }}>
      {visibleActivities.map(act => (
        <div key={act.key} className={classes.activityItem}>
          {React.createElement(
            act.icon, {
            style: { color: act.color, fontSize: '15px', marginRight: '2px' }
          })}
          <Text variant="subtitle2">
            {item[act.key]}
          </Text>
        </div>
      ))}
    </div>
  )
}, (prev, next) => {
  return ACTIVITY_INFO_LIST.every(act =>
    prev.item[act.key] === next.item[act.key]
  )
})

const UserField = memo(({ itemKey, fieldKey, userInfo }) => {
  let agentName = ''
  if (itemKey) {
    if (itemKey.uid === userInfo?.uid)
      agentName = "Me"
    else if (Array.isArray(itemKey))
      agentName = itemKey.map(i => `${i.first_name || ''} ${i.last_name || ''}`).join(', ')
    else
      agentName = `${itemKey.first_name || ''} ${itemKey.last_name || ''}`

    if (fieldKey === 'assigned_to') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
          <div style={{ display: 'inline-block', position: 'relative', top: '-1.5px', marginRight: 4 }}>
            <AvatarInitial user={itemKey} size="xs" />
          </div>
          {agentName}
        </span>
      )
    }
    return agentName
  } else {
    return fieldKey === 'assigned_to' ? "Unassigned" : null
  }
}, (prev, next) => prev.itemKey === next.itemKey && prev.fieldKey === next.fieldKey)

const StatusField = memo(({ itemKey }) => {
  return (
    <div style={{ display: 'flex' }}>
      <MuiBox>
        <Text
          variant="body2"
          noWrap={true}
          textCustomProps={getStyleProgressStatus(itemKey)}
        >
          {itemKey?.name}
        </Text>
      </MuiBox>
    </div>
  )
}, (prev, next) => prev.itemKey === next.itemKey)

const PriorityField = memo(({ itemKey, multiple }) => {
  let priorityValue = multiple ?
    itemKey?.map(f => <Chip key={f.name} label={f.name} style={{ margin: '3px 5px 3px 0' }} />) : itemKey?.name

  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
      {['normal', 'low', 'high', 'trivial'].includes(itemKey?.key) && (
        <div style={getPriorityStyle(itemKey?.key)}>
          <PriorityIcon fontSize="inherit" />
        </div>
      )}
      {itemKey?.key === 'critical' && (
        <div style={{ ...getPriorityStyle(itemKey?.key), fontSize: '0.7rem' }}>
          ‼️
        </div>
      )}
      {itemKey?.key === 'blocker' && (
        <div style={{ ...getPriorityStyle(itemKey?.key), fontSize: '0.9rem' }}>
          🚫
        </div>
      )}
      {priorityValue}
    </span>
  )
}, (prev, next) => prev.itemKey === next.itemKey && prev.multiple === next.multiple)

const OptionField = memo(({ itemKey, multiple }) => {
  if (multiple) {
    return itemKey?.map(f => <Chip key={f.name} label={f.name} style={{ margin: '3px 5px 3px 0' }} />)
  }
  return itemKey?.name
}, (prev, next) => prev.itemKey === next.itemKey && prev.multiple === next.multiple)

const MediaField = memo(({ itemKey }) => {
  if (!itemKey) return null
  return <FileListView value={itemKey} useSmallThumbs disableBorder />
}, (prev, next) => prev.itemKey === next.itemKey)

const TimeRangeField = memo(({ itemKey, endValue }) => {
  return (
    <Text variant="body2" noWrap={true}>
      {moment(itemKey).format('HH:mm')}{endValue ? ` - ${moment(endValue).format('HH:mm')}` : ''}
    </Text>
  )
}, (prev, next) => prev.itemKey === next.itemKey && prev.endValue === next.endValue)

const RichTextField = memo(({ itemKey }) => {
  if (!itemKey?.length) return null
  return (
    <Text
      variant="body2"
      lineCount={4}
      maxLine={4}
      textCustomProps={{ whiteSpace: 'pre-line' }}>
      {itemKey}
    </Text>
  )
}, (prev, next) => prev.itemKey === next.itemKey)

const IssueField = memo(({ itemKey }) => {
  if (!itemKey) return null
  const items = Array.isArray(itemKey) ? itemKey : [itemKey]
  return items?.map(f => <Chip key={f.key} label={f.key} style={{ margin: '3px 5px 3px 0' }} />)
}, (prev, next) => prev.itemKey === next.itemKey)

// Optimized field data computation with caching
const useFieldDataComputer = (item, fields, classes, appContext) => {
  const getFieldData = useCallback((field) => {
    if (!field) return null

    // Handle render function
    if (field.render) {
      return field.render(item, field)
    }

    // Handle component
    if (field.component) {
      return renderFieldComponent(item, field, classes)
    }

    const itemKey = item[field.key]

    // Early returns for null/undefined values
    if (itemKey === null || itemKey === undefined) {
      if (field.field_type === 'user' && field.key === 'assigned_to') {
        return "Unassigned"
      }
      return null
    }

    const fieldType = getFieldType(field.field_type)

    // Use memoized components for complex fields
    let fieldValue = null

    switch (fieldType) {
      case 'contact':
        fieldValue = getUserName(itemKey)
        break

      case 'company':
        fieldValue = itemKey?.name
        break

      case 'company_contact':
        fieldValue = `${getUserName(itemKey?.contact)} ${itemKey?.company ? `from ${itemKey.company.name}` : ''}`
        break

      case 'option':
        fieldValue = <OptionField itemKey={itemKey} multiple={field.multiple_value} />
        break

      case 'custom':
        if (typeof itemKey === 'object') {
          fieldValue = itemKey?.name || itemKey?.title || itemKey?.value || ''
        }
        break

      case 'priority':
        fieldValue = <PriorityField itemKey={itemKey} multiple={field.multiple_value} />
        break

      case 'user':
        fieldValue = <UserField itemKey={itemKey} fieldKey={field.key} userInfo={appContext.userInfo} />
        break

      case 'status':
        fieldValue = <StatusField itemKey={itemKey} />
        break

      case 'float':
        fieldValue = itemKey ? new Intl.NumberFormat('ID').format(itemKey) : ''
        break

      case 'date':
        if (moment(itemKey, moment.ISO_8601, true).isValid() && !field.format) {
          fieldValue = formatValue(itemKey, 'datetime|DD MMM yyy')
        }
        break

      case 'media':
        fieldValue = <MediaField itemKey={itemKey} />
        break

      case 'time':
        fieldValue = <Text variant="body2" noWrap={true} textCustomProps={field?.text_props || {}}>{moment(itemKey).format('HH:mm')}</Text>
        break

      case 'time_range_start':
        const timeRangeEndField = fields?.body?.find(f => f?.field_type === 'time_range_end')?.key
        const endValue = timeRangeEndField ? item[timeRangeEndField] : null
        fieldValue = <TimeRangeField itemKey={itemKey} endValue={endValue} />
        break

      case 'time_range_end':
        return null

      case 'richtext':
        fieldValue = <RichTextField itemKey={itemKey} />
        break

      case 'issue':
        fieldValue = <IssueField itemKey={itemKey} />
        break

      default:
        fieldValue = formatValue(itemKey, field.format)
    }

    // Apply custom color styling for string/number values
    if (['string', 'number'].includes(typeof fieldValue) && field.field_type !== 'status') {
      const hasAnyColor = !!(
        resolveColor(field.color, fieldValue) ||
        resolveColor(field.bg_color, fieldValue)
      )

      if (hasAnyColor) {
        const valueHolder = fieldValue
        fieldValue = (
          <span key="custom-color-span" style={getCustomTextStyle(field?.color, field?.bg_color, valueHolder)}>
            {valueHolder}
          </span>
        )
      }
    }

    if (!fieldValue) return null

    // Wrap with label if needed
    const padding = getFieldPadding(field.field_type, field.key)
    return (field?.label && fieldValue) ? (
      <div style={{ paddingTop: padding }}>
        <Text variant="body2" lineCount={fieldType === 'issue' ? 4 : undefined} maxLine={fieldType === 'issue' ? 4 : undefined}>
          {field.label}: {fieldValue}
        </Text>
      </div>
    ) : (
      <div style={{ paddingTop: padding }}>
        {fieldValue}
      </div>
    )
  }, [item, appContext.userInfo, fields, classes])

  return getFieldData
}

// Extract computed fields logic
const useComputedFields = (item, fields, getFieldData) => {
  return useMemo(() => {
    if (!fields) return {}

    const compute = (fieldConfig) => {
      return fieldConfig ? getFieldData(fieldConfig) : null
    }

    const result = {}

    if (fields.title) {
      result.title = {
        primary: compute(fields.title.primary),
        secondary: compute(fields.title.secondary),
      }
    }

    if (fields.subtitle) {
      result.subtitle = {
        primary: compute(fields.subtitle.primary),
        secondary: compute(fields.subtitle.secondary),
      }
    }

    // Only compute body if it exists and has items
    if (fields.body?.length) {
      result.body = fields.body.map((field) => compute(field))
    }

    if (fields.footer) {
      result.footer = {
        primary: compute(fields.footer.primary),
        secondary: compute(fields.footer.secondary),
      }
    }

    return result
  }, [fields, getFieldData])
}

const CardListItem = memo(({
  item,
  index,
  fields,
  variant,
  selectedIndex,
  draggableId,
  showCheckBox,
  isRowSelected,
  onRowSelect,
  onCardItemClick,
  renderCardItem,
  setHoveredIndex,
  hoveredIndex,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const appContext = useAppContext()

  const getFieldData = useFieldDataComputer(item, fields, classes, appContext)
  const computedFieldData = useComputedFields(item, fields, getFieldData)

  const cardContent = useMemo(() => {
    return (
      <BoardCardItem
        item={item}
        index={index}
        fields={fields}
        selected={index === selectedIndex}
        onClick={onCardItemClick(item)}
        draggableId={draggableId}
        computedFields={computedFieldData}
      />
    )
  }, [item, index, selectedIndex, fields, onCardItemClick, draggableId, computedFieldData])

  if (renderCardItem) {
    return renderCardItem(item, index, {
      variant: variant,
      selected: index === selectedIndex,
      index: index,
      onClick: onCardItemClick(item),
      draggableId,
      onMouseEnter: () => setHoveredIndex(index),
      onMouseLeave: () => setHoveredIndex(null),
      style: (hoveredIndex === index) && (index !== selectedIndex)
        ? { backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: theme.shape.borderRadius }
        : {},
    })
  }

  return (
    <div className={classes.selectableCardWrapper}>
      {showCheckBox && (
        <div className={classes.listCheckbox}>
          <Checkbox
            checked={isRowSelected && isRowSelected(item, index)}
            onChange={(event) => onRowSelect && onRowSelect(item, index)}
          />
        </div>
      )}
      {cardContent}
    </div>
  )
}, (prevProps, nextProps) => {
  return (
    prevProps.item === nextProps.item &&
    prevProps.index === nextProps.index &&
    prevProps.selectedIndex === nextProps.selectedIndex &&
    prevProps.fields === nextProps.fields &&
    prevProps.variant === nextProps.variant &&
    prevProps.showCheckBox === nextProps.showCheckBox &&
    prevProps.onRowSelect === nextProps.onRowSelect &&
    prevProps.isRowSelected === nextProps.isRowSelected &&
    prevProps.draggableId === nextProps.draggableId &&
    prevProps.renderCardItem === nextProps.renderCardItem &&
    prevProps.hoveredIndex === nextProps.hoveredIndex
  )
})

const CardList = memo((props) => {
  const theme = useTheme()
  const classes = useStyles()
  const { fields, skeleton = 0, renderCardItem, itemListRef, items, selectedIndex, onCardClick, variant, isFetching, ...listProps } = props
  const { showCheckBox, onRowSelect, isRowSelected } = props

  const [hoveredIndex, setHoveredIndex] = useState(null)

  const onCardItemClick = useCallback((item) => () => {
    onCardClick && onCardClick(item)
  }, [onCardClick])

  const renderCardSkeleton = useCallback((index) => {
    if (renderCardItem)
      return renderCardItem(undefined, index, { skeleton: 1 })

    return (
      <div key={`skeleton-${index}`} style={{ backgroundColor: 'white', padding: 8, borderRadius: 6 }}>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    )
  }, [renderCardItem])

  const renderedItems = useMemo(() => {
    if (skeleton) {
      return [...Array(skeleton)].map((_, index) => renderCardSkeleton(index))
    }

    if (!items || items.length === 0) return null

    const cardItems = items.map((item, index) => {
      const draggableId = listProps.droppableId ? `${listProps.droppableId}-${index}` : undefined

      return (
        <React.Fragment key={item.uid || item.id || index}>
          {index > 0 && variant === "card" && <div className={classes.cardSeparator} />}
          <div
            ref={ref => {
              if (itemListRef && itemListRef.current) itemListRef.current[index] = ref
            }}
          >
            <CardListItem
              item={item}
              index={index}
              fields={fields}
              variant={variant}
              selectedIndex={selectedIndex}
              draggableId={draggableId}
              showCheckBox={showCheckBox}
              isRowSelected={isRowSelected}
              onRowSelect={onRowSelect}
              onCardItemClick={onCardItemClick}
              renderCardItem={renderCardItem}
              setHoveredIndex={setHoveredIndex}
              hoveredIndex={hoveredIndex}
              theme={theme}
            />
          </div>
        </React.Fragment>
      )
    })

    return cardItems
  }, [
    items,
    skeleton,
    renderCardSkeleton,
    fields,
    variant,
    selectedIndex,
    showCheckBox,
    isRowSelected,
    onRowSelect,
    onCardItemClick,
    renderCardItem,
    hoveredIndex,
    theme,
    classes.cardSeparator,
    listProps.droppableId,
    itemListRef,
  ])

  return (
    <List variant={variant} {...listProps}>
      {renderedItems}

      {isFetching && (
        <Box display='flex' justifyContent='center' style={{ padding: theme.spacing(2) }}>
          <CircularProgress size='1.5rem' disableShrink={true} />
        </Box>
      )}
    </List>
  )
}, (prevProps, nextProps) => {
  return (
    prevProps.items === nextProps.items &&
    prevProps.skeleton === nextProps.skeleton &&
    prevProps.selectedIndex === nextProps.selectedIndex &&
    prevProps.isFetching === nextProps.isFetching &&
    prevProps.fields === nextProps.fields &&
    prevProps.variant === nextProps.variant &&
    prevProps.onCardClick === nextProps.onCardClick &&
    prevProps.showCheckBox === nextProps.showCheckBox &&
    prevProps.onRowSelect === nextProps.onRowSelect &&
    prevProps.isRowSelected === nextProps.isRowSelected
  )
})

export default CardList